import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useTranslation } from '@kostbar/i18n'
import { buildDeliveryColumns } from '../components/delivery-columns'
import { useDeliveryRestaurantStore } from '../store/delivery-restaurant-store'
import type {
  DeliveryListItem,
  DeliveryListStatus,
} from '@/api/endpoints/inventory-deliveries'
import { inventoryDeliveriesApi } from '@/api/endpoints/inventory-deliveries'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'
import { DataTable } from '@/components/data-table/data-table'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PermissionGate } from '@/shared/components/permission-gate'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'
import { useIsMobile } from '@/hooks/use-mobile'

/**
 * "Überfällig" is not a stored status but a flag derived in the store's timezone; the list
 * endpoint accepts it as a pseudo-status so the narrowing (and the paging) stays server-side.
 */
const STATUS_FILTERS = [
  'open',
  'overdue',
  'booked',
  'cancelled',
  'all',
] as const
type StatusFilter = (typeof STATUS_FILTERS)[number]

const STATUS_PARAM: Record<
  StatusFilter,
  Array<DeliveryListStatus> | undefined
> = {
  open: ['ANNOUNCED'],
  overdue: ['OVERDUE'],
  booked: ['BOOKED'],
  cancelled: ['CANCELLED'],
  all: undefined,
}

export function DeliveryListPage() {
  const { t } = useTranslation('inventory-deliveries')
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { can } = usePermissions()
  const canWrite = can(ACTIONS.INVENTORY_DELIVERY)
  const { data: restaurantsData } = useRestaurants()
  const restaurants = restaurantsData?.items ?? []
  const restaurantId = useDeliveryRestaurantStore((s) => s.restaurantId)
  const setRestaurantId = useDeliveryRestaurantStore((s) => s.setRestaurantId)
  const [status, setStatus] = useState<StatusFilter>('open')

  useEffect(() => {
    if (!restaurantId && restaurants.length > 0) {
      setRestaurantId(restaurants[0].id)
    }
  }, [restaurants, restaurantId, setRestaurantId])

  const columns = useMemo(
    () => buildDeliveryColumns(t, { compact: isMobile }),
    [t, isMobile],
  )

  async function fetchDeliveries({
    pageIndex,
    pageSize,
    q,
  }: {
    pageIndex: number
    pageSize: number
    q?: string
  }) {
    if (!restaurantId) return { rows: [], total: 0, pageCount: 0 }

    const res = await inventoryDeliveriesApi.list(restaurantId, {
      status: STATUS_PARAM[status],
      search: q || undefined,
      offset: pageIndex * pageSize,
      limit: pageSize,
    })

    return {
      rows: res.items,
      total: res.total,
      pageCount: Math.ceil(res.total / pageSize),
    }
  }

  return (
    <PermissionGate action={ACTIONS.INVENTORY_VIEW}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-xl font-semibold">{t('list.title')}</h1>
          {canWrite && (
            <Button asChild>
              <Link to="/inventory/deliveries/new">{t('list.new')}</Link>
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Select
            value={restaurantId != null ? String(restaurantId) : undefined}
            onValueChange={(v) => setRestaurantId(Number(v))}
          >
            <SelectTrigger className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {restaurants.map((r) => (
                <SelectItem key={r.id} value={String(r.id)}>
                  {r.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <ToggleGroup
            type="single"
            variant="outline"
            value={status}
            onValueChange={(v) => v && setStatus(v as StatusFilter)}
            aria-label={t('list.filter.label')}
          >
            {STATUS_FILTERS.map((value) => (
              <ToggleGroupItem key={value} value={value}>
                {t(`list.filter.${value}`)}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        <Card>
          <CardContent className="pt-6">
            <DataTable<DeliveryListItem>
              queryKey={`inventory-deliveries-${restaurantId ?? 'none'}-${status}`}
              columns={columns}
              fetcher={fetchDeliveries}
              globalSearch={{ placeholder: t('list.searchPlaceholder') }}
              selectable={false}
              showDensityToggle={false}
              enableColumnVisibility={false}
              initialState={{ pageSize: 25 }}
              getRowId={(row) => row.id}
              emptyState={
                <div className="flex flex-col items-center gap-3 py-8">
                  <p className="text-sm text-muted-foreground">
                    {t('list.empty')}
                  </p>
                  {canWrite && (
                    <Button asChild variant="outline">
                      <Link to="/inventory/deliveries/new">
                        {t('list.new')}
                      </Link>
                    </Button>
                  )}
                </div>
              }
              onRowClick={(row) =>
                navigate({
                  to: '/inventory/deliveries/$id',
                  params: { id: row.original.id },
                })
              }
            />
          </CardContent>
        </Card>
      </div>
    </PermissionGate>
  )
}
